/**
 * 管理画面のルートが必要とする施設スコープ。
 *
 * 基本は組織横断で閲覧できるものとして扱い、施設を決めずに表示・更新すると
 * 対象が曖昧になる画面だけを明示的に `single-facility` に分類する。
 * 新しい横断一覧を追加するたびに許可リストを更新する方式に戻さないこと。
 */
export type FacilityRouteCapability = "cross-facility" | "single-facility";

/**
 * 画面固有の `facilityId` と衝突させない、管理画面ワークスペースの施設範囲。
 */
export const FACILITY_SCOPE_QUERY_PARAM = "workspaceFacility";

const SINGLE_FACILITY_ROUTE_PATTERNS: readonly RegExp[] = [
  // URL自体に施設IDを含む施設詳細・編集・運用ページ。
  // 表示範囲を切り替えた際、ヘッダーと更新先の施設が食い違わないよう配下を一括で扱う。
  /^\/facilities\/(?!create(?:\/|$))[^/]+(?:\/|$)/,

  // 現在の施設を更新対象として利用する作成・設定画面。
  /^\/customers\/parents\/invite(?:\/|$)/,
  /^\/events\/create(?:\/|$)/,
  /^\/events\/[^/]+\/edit(?:\/|$)/,
  /^\/products\/inventory(?:\/|$)/,
  /^\/surveys(?:\/|$)/,
  /^\/trial-settings(?:\/|$)/,
  /^\/trial-form-templates(?:\/|$)/,
];

function normalizedPathname(pathname: string): string {
  const withoutQueryOrHash = pathname.split(/[?#]/, 1)[0];
  if (withoutQueryOrHash.length > 1 && withoutQueryOrHash.endsWith("/")) {
    return withoutQueryOrHash.slice(0, -1);
  }
  return withoutQueryOrHash;
}

/**
 * 施設詳細URLに明示された施設IDを返す。`/facilities/create` は施設IDではない。
 */
export function getFacilityScopeFromPath(
  pathname: string | null | undefined
): string | null {
  if (!pathname) return null;

  const match = normalizedPathname(pathname).match(
    /^\/facilities\/(?!create(?:\/|$))([^/]+)(?:\/|$)/
  );
  if (!match) return null;

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

export function getFacilityRouteCapability(
  pathname: string | null | undefined
): FacilityRouteCapability {
  if (!pathname) return "cross-facility";

  const normalized = normalizedPathname(pathname);
  return SINGLE_FACILITY_ROUTE_PATTERNS.some((pattern) =>
    pattern.test(normalized)
  )
    ? "single-facility"
    : "cross-facility";
}

export function requiresSingleFacility(
  pathname: string | null | undefined
): boolean {
  return getFacilityRouteCapability(pathname) === "single-facility";
}

export function isAllowedInAllFacilities(
  pathname: string | null | undefined
): boolean {
  return !requiresSingleFacility(pathname);
}

/**
 * `/facilities/:id/...` の施設セグメントだけを、安全な施設IDへ差し替える。
 *
 * 施設IDは必ず単一のURLセグメントとしてエンコードするため、外部入力に `/` や
 * クエリ文字が混じっても別ルートへ遷移しない。`all` は更新対象にできない。
 */
export function resolveFacilityScopedPath(
  pathname: string,
  facilityId: string
): string | null {
  const trimmedFacilityId = facilityId.trim();
  if (!trimmedFacilityId || trimmedFacilityId === "all") return null;

  const encodedFacilityId = encodeURIComponent(trimmedFacilityId);
  return pathname.replace(
    /^\/facilities\/[^/]+(?=\/|$)/,
    `/facilities/${encodedFacilityId}`
  );
}

/**
 * アプリ内URLへ現在のワークスペース施設を付与する。
 *
 * 既存のqueryとhashを保持するため、サイドメニューだけでなく検索・ピン留め・
 * クイック作成など、すべてのナビゲーション導線で同じ規則を利用する。
 */
export function appendFacilityScopeToUrl(
  url: string,
  facilityId: string
): string {
  const trimmedFacilityId = facilityId.trim();
  if (!trimmedFacilityId) return url;

  const hashIndex = url.indexOf("#");
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : "";
  const urlWithoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const queryIndex = urlWithoutHash.indexOf("?");
  const pathname =
    queryIndex >= 0 ? urlWithoutHash.slice(0, queryIndex) : urlWithoutHash;
  const search = queryIndex >= 0 ? urlWithoutHash.slice(queryIndex + 1) : "";
  const searchParams = new URLSearchParams(search);
  searchParams.set(FACILITY_SCOPE_QUERY_PARAM, trimmedFacilityId);

  return `${pathname}?${searchParams.toString()}${hash}`;
}
